import { useState, useEffect } from "react";
import { safeFormat } from '@/lib/safeDate';
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { DatePickerInput } from "@/components/ui/date-picker-input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from "@/components/ui/command";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { format } from "date-fns";
import { CalendarIcon, Loader2, ChevronsUpDown, X, Phone, User, Lock, Video, AlertCircle, Check, Users, Plus as PlusIcon, Zap } from "lucide-react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useGoogleCalendar } from '@/hooks/useGoogleCalendar';
import { useAppUsers } from '@/hooks/useAppUsers';
import { useSalesDevelopment } from '@/hooks/useSalesDevelopment';
import { MeetingConfirmationModal } from '@/components/meetings/MeetingConfirmationModal';
import { VideoRingProgressModal } from '@/components/calls/VideoRingProgressModal';

interface ScheduleCallModalProps {
  open: boolean;
  onClose: () => void;
  onSchedule: (callData: any) => Promise<void>;
  contactId?: string;
  leadId?: string;
  dealId?: string;
  prefilledPhone?: string;
  prefilledName?: string;
  onSuccess?: () => void;
  initialData?: any;
  isEditing?: boolean;
}

export function ScheduleCallModal({
  open,
  onClose,
  onSchedule,
  contactId,
  leadId,
  dealId,
  prefilledPhone,
  prefilledName,
  onSuccess,
  initialData,
  isEditing = false,
}: ScheduleCallModalProps) {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [twilioNumbers, setTwilioNumbers] = useState<any[]>([]);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [contacts, setContacts] = useState<any[]>([]);
  const [leads] = useState<any[]>([]); // DEPRECATED - leads table no longer used
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedContact, setSelectedContact] = useState<any>(null);
  const [selectedLead, setSelectedLead] = useState<any>(null);

  // Video call states
  const [sendGoogleInvite, setSendGoogleInvite] = useState(true);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmedMeeting, setConfirmedMeeting] = useState<any>(null);
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [availabilityStatus, setAvailabilityStatus] = useState<{
    checked: boolean;
    available: boolean;
    conflicts?: any[];
  } | null>(null);
  const [isHostConnected, setIsHostConnected] = useState(false);
  const [attendeeEmail, setAttendeeEmail] = useState('');
  
  // Immediate video ring states
  const [isStartingVideoRing, setIsStartingVideoRing] = useState(false);
  const [videoRingData, setVideoRingData] = useState<any>(null);
  const [hasLoadedData, setHasLoadedData] = useState(false);

  const [formData, setFormData] = useState({
    // Common fields
    channel: "voice" as "voice" | "video",
    scheduled_date: undefined as Date | undefined,
    scheduled_time: "",
    reminder: "none",
    
    // Voice call fields
    subject: "",
    to_e164: prefilledPhone || "",
    from_e164: "",
    call_purpose: "",
    call_agenda: "",
    handled_by_app_user_id: "",
    
    // Video call fields
    title: "",
    description: "",
    location: "",
    meeting_url: "",
    created_by: "",
    attendees: [] as string[],
  });

  const [fieldErrors, setFieldErrors] = useState<{
    subject?: boolean;
    title?: boolean;
    to_e164?: boolean;
    scheduled_date?: boolean;
    scheduled_time?: boolean;
  }>({});

  // Hooks
  const { users } = useAppUsers();
  const { salesDevelopment } = useSalesDevelopment();
  const { 
    isConnected, 
    checkConnection, 
    checkAvailability, 
    createCalendarEvent,
    updateCalendarEvent,
    deleteCalendarEvent,
  } = useGoogleCalendar();

  // Combine users
  const allUsers = [...(users || []), ...(salesDevelopment || [])]
    .filter((user, index, self) => 
      index === self.findIndex((u) => u.id === user.id)
    )
    .sort((a, b) => {
      const nameA = (a.display_name || a.email || '').toLowerCase();
      const nameB = (b.display_name || b.email || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });

  // Load data when modal opens (only once)
  useEffect(() => {
    if (open && !hasLoadedData) {
      console.log('📞 Schedule Call Modal opened with:', { contactId, leadId, dealId, prefilledPhone });
      loadData();
      setHasLoadedData(true);
    }
    if (!open) {
      setHasLoadedData(false);
    }
  }, [open, hasLoadedData]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      console.log('🔄 Resetting modal state - videoRingData will be cleared:', !!videoRingData);
      resetForm();
    }
  }, [open]);

  // Check host connection when host changes
  useEffect(() => {
    const updateHostConnection = async () => {
      if (formData.created_by && formData.channel === 'video') {
        const connected = await checkConnection(formData.created_by);
        setIsHostConnected(connected);
      } else {
        setIsHostConnected(false);
      }
    };
    
    updateHostConnection();
    setAvailabilityStatus(null);
  }, [formData.created_by, formData.channel]);

  // Check availability when date/time/host changes
  useEffect(() => {
    const checkHostAvailability = async () => {
      if (formData.channel !== 'video' || !formData.created_by || !formData.scheduled_date || !formData.scheduled_time) {
        setAvailabilityStatus(null);
        return;
      }

      setCheckingAvailability(true);
      
      try {
        const scheduledDateTime = new Date(formData.scheduled_date);
        const [hours, minutes] = formData.scheduled_time.split(':');
        scheduledDateTime.setHours(parseInt(hours), parseInt(minutes));
        
        const startTime = scheduledDateTime;
        const endTime = new Date(startTime.getTime() + 60 * 60000); // 60 minutes

        const result = await checkAvailability({
          host_user_id: formData.created_by,
          start_time: startTime.toISOString(),
          end_time: endTime.toISOString(),
        });

        if (result.success) {
          setAvailabilityStatus({
            checked: true,
            available: result.available,
            conflicts: result.conflicts || [],
          });
        }
      } catch (error) {
        console.error('Error checking availability:', error);
      } finally {
        setCheckingAvailability(false);
      }
    };

    const timeoutId = setTimeout(checkHostAvailability, 500);
    return () => clearTimeout(timeoutId);
  }, [formData.created_by, formData.scheduled_date, formData.scheduled_time, formData.channel]);

  // Load initialData when editing
  useEffect(() => {
    if (open && initialData && isEditing) {
      console.log('📝 Loading data for editing:', initialData);
      
      setFormData({
        channel: initialData.channel || 'voice',
        scheduled_date: initialData.scheduled_for ? new Date(initialData.scheduled_for) : undefined,
        scheduled_time: initialData.scheduled_for 
          ? safeFormat(new Date(initialData.scheduled_for), 'HH:mm')
          : '',
        reminder: 'none',
        subject: initialData.subject || '',
        title: initialData.title || '',
        to_e164: initialData.to_e164 || '',
        from_e164: initialData.from_e164 || '',
        call_purpose: initialData.call_purpose || '',
        call_agenda: initialData.call_agenda || '',
        description: initialData.description || '',
        handled_by_app_user_id: initialData.handled_by_app_user_id || '',
        created_by: initialData.created_by || initialData.handled_by_app_user_id || '',
        meeting_url: initialData.meeting_url || '',
        location: '',
        attendees: Array.isArray(initialData.attendees) ? initialData.attendees : [],
      });
    }
  }, [open, initialData, isEditing]);

  const loadData = async () => {
    console.log('🔄 Starting loadData()...');
    setIsLoadingData(true);
    
    try {
      // Load current user
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: appUser } = await supabase
          .from('app_users')
          .select('*')
          .eq('auth_user_id', user.id)
          .single();
        
        if (appUser) {
          setCurrentUser(appUser);
          // Only set user fields if they're empty to avoid overwriting user input
          setFormData(prev => ({
            ...prev,
            handled_by_app_user_id: prev.handled_by_app_user_id || appUser.id,
            from_e164: prev.from_e164 || appUser.twilio_phone_number || "",
            created_by: prev.created_by || appUser.id,
          }));
        }
      }

      // Load contacts with country code
      const { data: contactsData } = await supabase
        .from('contacts')
        .select('id, first_name, last_name, primary_phone, primary_phone_country_code, primary_email')
        .order('first_name');
      
      console.log('📋 Loaded contacts:', contactsData?.length || 0);
      if (contactsData) setContacts(contactsData);

      // DEPRECATED - leads query removed

      // Load Twilio numbers
      const { data: numbers } = await supabase.functions.invoke('twilio-list-numbers');
      if (numbers?.phoneNumbers) {
        setTwilioNumbers(numbers.phoneNumbers);
      }

      // Pre-select contact AFTER data is loaded
      if (contactId && contactsData) {
        let contact = contactsData.find(c => c.id === contactId);
        
        if (!contact) {
          console.log('🔎 Contact not in first page, fetching by id...');
          const { data: contactById } = await supabase
            .from('contacts')
            .select('id, first_name, last_name, primary_phone, primary_phone_country_code, primary_email')
            .eq('id', contactId)
            .single();
          
          if (contactById) {
            contact = contactById;
            console.log('✅ Fetched contact by ID:', contact);
          }
        }
        
        if (contact) {
          console.log('✅ Pre-selecting contact:', contact);
          
          const phoneNumber = contact.primary_phone_country_code && contact.primary_phone
            ? `${contact.primary_phone_country_code}${contact.primary_phone}`.trim()
            : (contact.primary_phone || '');
          
          console.log('📞 Setting phone number:', phoneNumber);
          
          setSelectedContact(contact);
          // Only set phone if it's empty to avoid overwriting user input
          setFormData(prev => {
            const newFormData = { 
              ...prev, 
              to_e164: prev.to_e164 || phoneNumber
            };
            console.log('📝 Updated formData.to_e164:', newFormData.to_e164);
            return newFormData;
          });

          // Add contact email to attendees if video call and if attendees is empty
          if (contact.primary_email) {
            setFormData(prev => ({
              ...prev,
              attendees: prev.attendees.length > 0 ? prev.attendees : [contact.primary_email]
            }));
          }
        } else {
          console.warn('⚠️ Contact not found. ContactId:', contactId);
        }
      }
      
      // DEPRECATED - lead pre-selection removed
      
      console.log('✅ loadData() completed');
    } catch (error) {
      console.error('❌ Error loading data:', error);
    } finally {
      setIsLoadingData(false);
    }
  };

  const handleVoiceCallSubmit = async (scheduledDateTime: Date) => {
    let reminderTime = null;
    if (formData.reminder !== 'none') {
      reminderTime = new Date(scheduledDateTime);
      switch (formData.reminder) {
        case '15min':
          reminderTime.setMinutes(reminderTime.getMinutes() - 15);
          break;
        case '30min':
          reminderTime.setMinutes(reminderTime.getMinutes() - 30);
          break;
        case '1hour':
          reminderTime.setHours(reminderTime.getHours() - 1);
          break;
        case '1day':
          reminderTime.setDate(reminderTime.getDate() - 1);
          break;
      }
    }

    const callData = {
      contact_id: selectedContact?.id || contactId,
      lead_id: selectedLead?.id || leadId,
      deal_id: dealId,
      title: formData.subject || null,
      to_e164: formData.to_e164,
      from_e164: formData.from_e164,
      call_purpose: formData.call_purpose || null,
      call_agenda: formData.call_agenda || null,
      scheduled_for: scheduledDateTime.toISOString(),
      handled_by_app_user_id: formData.handled_by_app_user_id || null,
      reminder_time: reminderTime?.toISOString() || null,
      channel: 'voice' as const,
      direction: 'outbound',
      provider: 'manual',
      status: 'scheduled',
      created_by: formData.handled_by_app_user_id || null,
      scheduled_by: currentUser?.id || null, // Track who scheduled the call (SDR)
    };

    if (isEditing && initialData?.id) {
      // UPDATE existing call
      const { error } = await supabase
        .from('calls')
        .update(callData)
        .eq('id', initialData.id);
        
      if (error) throw error;
      toast.success(t('scheduleCall.callUpdated'));
    } else {
      // INSERT new call
      await onSchedule(callData);
      toast.success(t('scheduleCall.voiceCallScheduled'));
    }
    
    onSuccess?.();
    onClose();
    resetForm();
  };

  const handleVideoCallSubmit = async (scheduledDateTime: Date) => {
    const callData = {
      channel: 'video' as const,
      direction: 'outbound',
      provider: 'manual',
      contact_id: selectedContact?.id || contactId || null,
      lead_id: selectedLead?.id || leadId || null,
      deal_id: dealId || null,
      title: formData.title,
      notes: formData.description || null,
      meeting_url: formData.meeting_url || null,
      scheduled_for: scheduledDateTime.toISOString(),
      status: 'scheduled',
      attendees: formData.attendees as any,
      created_by: formData.created_by || null, // HOST (Anfitrião/Consultant) - quem vai conduzir a reunião
      scheduled_by: currentUser?.id || null,   // SDR - quem agendou a reunião (usuário logado)
      metadata: { location: formData.location } as any,
    };

    if (isEditing && initialData?.id) {
      // UPDATE existing video call in database
      const updatePayload = {
        ...callData,
        handled_by_app_user_id: formData.created_by, // Keep in sync with created_by for video
      };
      
      const { error } = await supabase
        .from('calls')
        .update(updatePayload)
        .eq('id', initialData.id);
        
      if (error) throw error;

      const endTime = new Date(scheduledDateTime.getTime() + 60 * 60000);
      const hostChanged = initialData.created_by !== formData.created_by;
      const hasExistingCalendarEvent = initialData.google_calendar_event_id;

      // Handle Google Calendar sync
      if (sendGoogleInvite) {
        try {
          if (hostChanged && hasExistingCalendarEvent && initialData.created_by) {
            // HOST CHANGED: Delete from old host, create in new host
            console.log('Host changed from', initialData.created_by, 'to', formData.created_by);
            
            // 1. Delete from old host's calendar
            try {
              await deleteCalendarEvent({
                host_user_id: initialData.created_by,
                event_id: initialData.google_calendar_event_id,
              });
              console.log('Deleted event from old host calendar');
            } catch (deleteError) {
              console.warn('Could not delete from old calendar:', deleteError);
            }

            // 2. Create in new host's calendar (if connected)
            if (isHostConnected) {
              const result = await createCalendarEvent({
                host_user_id: formData.created_by,
                title: formData.title,
                description: formData.description,
                start_time: scheduledDateTime.toISOString(),
                end_time: endTime.toISOString(),
                location: formData.location,
                attendees: formData.attendees,
                meeting_id: initialData.id,
              });

              // Update with new calendar event info
              if (result?.event_id) {
                await supabase
                  .from('calls')
                  .update({ 
                    google_calendar_event_id: result.event_id,
                    google_calendar_html_link: result.html_link,
                    meeting_url: result.meet_link || formData.meeting_url,
                  })
                  .eq('id', initialData.id);
              }
            }
          } else if (hasExistingCalendarEvent && isHostConnected) {
            // HOST SAME: Just update the existing event
            await updateCalendarEvent({
              host_user_id: formData.created_by,
              event_id: initialData.google_calendar_event_id,
              title: formData.title,
              description: formData.description,
              start_time: scheduledDateTime.toISOString(),
              end_time: endTime.toISOString(),
              location: formData.location,
              attendees: formData.attendees,
            });
          } else if (!hasExistingCalendarEvent && isHostConnected) {
            // NO EXISTING EVENT: Create new one
            const result = await createCalendarEvent({
              host_user_id: formData.created_by,
              title: formData.title,
              description: formData.description,
              start_time: scheduledDateTime.toISOString(),
              end_time: endTime.toISOString(),
              location: formData.location,
              attendees: formData.attendees,
              meeting_id: initialData.id,
            });

            if (result?.event_id) {
              await supabase
                .from('calls')
                .update({ 
                  google_calendar_event_id: result.event_id,
                  google_calendar_html_link: result.html_link,
                  meeting_url: result.meet_link || formData.meeting_url,
                })
                .eq('id', initialData.id);
            }
          }
        } catch (calError) {
          console.error('Calendar sync error:', calError);
          toast.warning('Reunião atualizada, mas houve erro ao sincronizar calendário');
        }
      }

      // Sync internal calendar_events table
      try {
        const originalStart = initialData.scheduled_for;
        if (originalStart) {
          let ceQuery = supabase
            .from('calendar_events')
            .select('id')
            .eq('start_at', originalStart);

          if (initialData.contact_id) {
            ceQuery = ceQuery.eq('contact_id', initialData.contact_id);
          }
          if (initialData.title) {
            ceQuery = ceQuery.eq('title', initialData.title);
          }

          const { data: ceMatch } = await ceQuery.maybeSingle();

          if (ceMatch) {
            await supabase
              .from('calendar_events')
              .update({
                title: formData.title,
                description: formData.description || null,
                start_at: scheduledDateTime.toISOString(),
                end_at: endTime.toISOString(),
                updated_at: new Date().toISOString(),
              })
              .eq('id', ceMatch.id);

            console.log('calendar_events synced for video call edit:', ceMatch.id);
          }
        }
      } catch (ceSyncError) {
        console.warn('Could not sync calendar_events on video call edit:', ceSyncError);
      }

      toast.success(t('scheduleCall.videoMeetingUpdated'));
      onSuccess?.();
      onClose();
      resetForm();
      return;
    }

    // INSERT new video call
    const { data: newCall, error } = await supabase
      .from("calls")
      .insert({ ...callData, handled_by_app_user_id: formData.created_by || null })
      .select()
      .single();

    if (error) throw error;

    let finalMeetingData = newCall;

    if (sendGoogleInvite && isHostConnected && newCall) {
      const endTime = new Date(scheduledDateTime.getTime() + 60 * 60000);

      try {
        const result = await createCalendarEvent({
          host_user_id: formData.created_by,
          title: formData.title,
          description: formData.description,
          start_time: scheduledDateTime.toISOString(),
          end_time: endTime.toISOString(),
          location: formData.location,
          attendees: formData.attendees,
          meeting_id: newCall.id,
        });

        if (result?.meet_link) {
          await supabase
            .from('calls')
            .update({ meeting_url: result.meet_link })
            .eq('id', newCall.id);
          
          finalMeetingData = {
            ...newCall,
            meeting_url: result.meet_link
          };
        }
      } catch (calError) {
        console.error('Calendar event error:', calError);
        toast.warning(t('scheduleCall.calendarInviteFailed'));
      }
    }

    // Also create a calendar_events entry so it appears in the Agenda
    try {
      const endTime = new Date(scheduledDateTime.getTime() + 60 * 60000);
      const attendeeIds: string[] = [];
      if (formData.created_by) attendeeIds.push(formData.created_by);
      if (currentUser?.id && !attendeeIds.includes(currentUser.id)) attendeeIds.push(currentUser.id);

      await (supabase as any)
        .from('calendar_events')
        .insert({
          title: formData.title,
          description: formData.description || null,
          start_at: scheduledDateTime.toISOString(),
          end_at: endTime.toISOString(),
          all_day: false,
          type: 'meeting',
          status: 'scheduled',
          color: null,
          created_by: formData.created_by || currentUser?.id || null,
          attendee_ids: attendeeIds,
          contact_id: selectedContact?.id || contactId || null,
          deal_id: dealId || null,
          daily_room_url: finalMeetingData.meeting_url || null,
          daily_room_name: finalMeetingData.meeting_url?.includes('daily.co') 
            ? finalMeetingData.meeting_url.split('/').pop() || null 
            : null,
        });
      console.log('✅ Calendar event created for scheduled video call');
    } catch (calEvtError) {
      console.error('Error creating calendar_events entry:', calEvtError);
    }

    setConfirmedMeeting({
      title: formData.title,
      scheduled_at: scheduledDateTime.toISOString(),
      meeting_url: finalMeetingData.meeting_url,
      attendees: formData.attendees,
    });
    setShowConfirmation(true);
    
    toast.success(t('scheduleCall.videoMeetingScheduled'));
    onSuccess?.();
    onClose();
    resetForm();
  };

  const handleSubmit = async () => {
    const errors: typeof fieldErrors = {};
    
    if (formData.channel === 'video') {
      if (!formData.title?.trim()) {
        errors.title = true;
        toast.error(t('scheduleCall.errors.meetingTitleRequired'));
        setFieldErrors(errors);
        return;
      }
      if (!formData.created_by) {
        toast.error(t('scheduleCall.errors.hostRequired'));
        return;
      }
    } else {
      if (!formData.to_e164?.trim()) {
        errors.to_e164 = true;
        toast.error(t('scheduleCall.errors.phoneRequired'));
        setFieldErrors(errors);
        return;
      }
    }

    if (!formData.scheduled_date) {
      errors.scheduled_date = true;
      toast.error(t('scheduleCall.errors.dateRequired'));
      setFieldErrors(errors);
      return;
    }

    if (!formData.scheduled_time) {
      errors.scheduled_time = true;
      toast.error(t('scheduleCall.errors.timeRequired'));
      setFieldErrors(errors);
      return;
    }

    setFieldErrors({});
    setIsSubmitting(true);

    try {
      const scheduledDateTime = new Date(formData.scheduled_date);
      const [hours, minutes] = formData.scheduled_time.split(':');
      scheduledDateTime.setHours(parseInt(hours), parseInt(minutes));

      if (formData.channel === 'video') {
        await handleVideoCallSubmit(scheduledDateTime);
      } else {
        await handleVoiceCallSubmit(scheduledDateTime);
      }
    } catch (error) {
      console.error("Error scheduling call:", error);
      toast.error(t('scheduleCall.errors.failedToSchedule'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStartVideoRingNow = async () => {
    // Validate required fields
    if (!formData.title?.trim()) {
      toast.error(t('scheduleCall.errors.meetingTitleRequired'));
      return;
    }
    
    if (!selectedContact && !selectedLead) {
      toast.error(t('scheduleCall.errors.selectContactOrLead'));
      return;
    }

    setIsStartingVideoRing(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('start-video-ring', {
        body: {
          contact_id: selectedContact?.id || contactId,
          lead_id: selectedLead?.id || leadId,
          deal_id: dealId,
          title: formData.title,
          description: formData.description,
          call_agenda: formData.description,
        },
      });

      if (error) throw error;

      console.log('[ScheduleCall] ✅ Video ring data received:', data);
      setVideoRingData(data);
      console.log('[ScheduleCall] ✅ videoRingData state updated');
      toast.success(t('scheduleCall.videoRingStarted'));
    } catch (error) {
      console.error('Error starting video ring:', error);
      toast.error(error instanceof Error ? error.message : t('scheduleCall.errors.failedToStartVideoRing'));
    } finally {
      setIsStartingVideoRing(false);
    }
  };

  const resetForm = () => {
    setSelectedContact(null);
    setSelectedLead(null);
    setSearchQuery("");
    setFormData({
      channel: "voice",
      scheduled_date: undefined,
      scheduled_time: "",
      reminder: "none",
      subject: "",
      to_e164: prefilledPhone || "",
      from_e164: currentUser?.twilio_phone_number || "",
      call_purpose: "",
      call_agenda: "",
      handled_by_app_user_id: currentUser?.id || "",
      title: "",
      description: "",
      location: "",
      meeting_url: "",
      created_by: currentUser?.id || "",
      attendees: [],
    });
    setFieldErrors({});
    setSendGoogleInvite(true);
    setAttendeeEmail('');
    setAvailabilityStatus(null);
    setIsHostConnected(false);
    setVideoRingData(null);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {formData.channel === 'video' ? t('scheduleCall.scheduleVideoMeeting') : t('scheduleCall.scheduleVoiceCall')}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Call Type Selector */}
            <div className="space-y-2">
              <Label>{t('scheduleCall.callType')} *</Label>
              <Select
                value={formData.channel}
                onValueChange={(value: "voice" | "video") => 
                  setFormData({ ...formData, channel: value })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="voice">
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4" />
                      {t('scheduleCall.voiceCall')}
                    </div>
                  </SelectItem>
                  <SelectItem value="video">
                    <div className="flex items-center gap-2">
                      <Video className="w-4 h-4" />
                      {t('scheduleCall.videoCall')}
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Contact/Lead Selection (both types) */}
            {!contactId && !leadId && (
              <div className="space-y-2">
                <Label>{t('scheduleCall.callFor')}</Label>
                <Popover open={searchOpen} onOpenChange={setSearchOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      className="w-full justify-between"
                    >
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-muted-foreground" />
                        <span>
                          {selectedContact 
                            ? `${selectedContact.first_name} ${selectedContact.last_name || ''}`.trim()
                            : selectedLead
                            ? `${selectedLead.first_name} ${selectedLead.last_name || ''}`.trim()
                            : t('scheduleCall.selectContactOrLead')}
                        </span>
                      </div>
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0" align="start">
                    <Command>
                      <CommandInput 
                        placeholder={t('common.search')} 
                        value={searchQuery}
                        onValueChange={setSearchQuery}
                      />
                      <CommandEmpty>{t('common.noResultsFound')}</CommandEmpty>
                      
                      <CommandGroup heading={t('scheduleCall.contacts')}>
                        {contacts
                          .filter(c => 
                            `${c.first_name} ${c.last_name || ''} ${c.primary_email || ''}`
                              .toLowerCase()
                              .includes(searchQuery.toLowerCase())
                          )
                          .slice(0, 10)
                          .map((contact) => (
                            <CommandItem
                              key={contact.id}
                              value={`${contact.first_name || ''} ${contact.last_name || ''}`.toLowerCase()}
                              onSelect={() => {
                                setSelectedContact(contact);
                                setSelectedLead(null);
                                const phone = contact.primary_phone_country_code && contact.primary_phone
                                  ? `${contact.primary_phone_country_code}${contact.primary_phone}`.trim()
                                  : (contact.primary_phone || '');
                                setFormData(prev => ({
                                  ...prev,
                                  to_e164: phone,
                                  attendees: contact.primary_email ? [contact.primary_email] : []
                                }));
                                setSearchOpen(false);
                              }}
                            >
                              {`${contact.first_name || ''} ${contact.last_name || ''}`.trim()}
                            </CommandItem>
                          ))}
                      </CommandGroup>
                      
                      <CommandGroup heading={t('scheduleCall.leads')}>
                        {leads
                          .filter(l => 
                            `${l.first_name} ${l.last_name || ''} ${l.primary_email || ''}`
                              .toLowerCase()
                              .includes(searchQuery.toLowerCase())
                          )
                          .slice(0, 10)
                          .map((lead) => (
                            <CommandItem
                              key={lead.id}
                              value={`${lead.first_name || ''} ${lead.last_name || ''}`.toLowerCase()}
                              onSelect={() => {
                                setSelectedLead(lead);
                                setSelectedContact(null);
                                setFormData(prev => ({
                                  ...prev,
                                  to_e164: lead.primary_phone || '',
                                  attendees: lead.primary_email ? [lead.primary_email] : []
                                }));
                                setSearchOpen(false);
                              }}
                            >
                              {`${lead.first_name || ''} ${lead.last_name || ''}`.trim()}
                            </CommandItem>
                          ))}
                      </CommandGroup>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
            )}

            {/* VOICE CALL FIELDS */}
            {formData.channel === 'voice' && (
              <>
                <div className="space-y-2">
                  <Label>{t('scheduleCall.subject')}</Label>
                  <Input
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    placeholder={t('scheduleCall.placeholders.subject')}
                  />
                </div>

                <div className="space-y-2">
                  <Label>{t('scheduleCall.contactPhone')} *</Label>
                  <Input
                    value={formData.to_e164}
                    onChange={(e) => {
                      setFormData({ ...formData, to_e164: e.target.value });
                      setFieldErrors(prev => ({ ...prev, to_e164: false }));
                    }}
                    placeholder="+1234567890"
                    className={cn(fieldErrors.to_e164 && "border-red-500")}
                  />
                  {fieldErrors.to_e164 && (
                    <p className="text-xs text-red-500">{t('scheduleCall.errors.phoneRequired')}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>{t('scheduleCall.callPurpose')}</Label>
                  <Input
                    value={formData.call_purpose}
                    onChange={(e) => setFormData({ ...formData, call_purpose: e.target.value })}
                    placeholder={t('scheduleCall.placeholders.callPurpose')}
                  />
                </div>

                <div className="space-y-2">
                  <Label>{t('scheduleCall.callAgenda')}</Label>
                  <Textarea
                    value={formData.call_agenda}
                    onChange={(e) => setFormData({ ...formData, call_agenda: e.target.value })}
                    placeholder={t('scheduleCall.placeholders.callAgenda')}
                    rows={3}
                  />
                </div>

                <div className="space-y-2">
                  <Label>{t('scheduleCall.reminder')}</Label>
                  <Select
                    value={formData.reminder}
                    onValueChange={(value) => setFormData({ ...formData, reminder: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">{t('common.none')}</SelectItem>
                      <SelectItem value="15min">{t('scheduleCall.reminders.15min')}</SelectItem>
                      <SelectItem value="30min">{t('scheduleCall.reminders.30min')}</SelectItem>
                      <SelectItem value="1hour">{t('scheduleCall.reminders.1hour')}</SelectItem>
                      <SelectItem value="1day">{t('scheduleCall.reminders.1day')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            {/* VIDEO CALL FIELDS */}
            {formData.channel === 'video' && (
              <>
                <div className="space-y-2">
                  <Label>{t('scheduleCall.meetingTitle')} *</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => {
                      setFormData({ ...formData, title: e.target.value });
                      setFieldErrors(prev => ({ ...prev, title: false }));
                    }}
                    className={cn(fieldErrors.title && "border-red-500")}
                    placeholder={t('scheduleCall.placeholders.meetingTitle')}
                  />
                  {fieldErrors.title && (
                    <p className="text-xs text-red-500">{t('scheduleCall.errors.meetingTitleRequired')}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>{t('scheduleCall.host')} *</Label>
                  <Select
                    value={formData.created_by}
                    onValueChange={(value) => setFormData({ ...formData, created_by: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t('scheduleCall.selectHost')} />
                    </SelectTrigger>
                    <SelectContent>
                      {allUsers.map((user) => (
                        <SelectItem key={user.id} value={user.id}>
                          {user.display_name || user.email}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {formData.created_by && !isHostConnected && !checkingAvailability && (
                  <div className="p-4 border border-orange-200 rounded-lg bg-orange-50/50">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-orange-600 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-orange-900">
                          {t('scheduleCall.hostNotConnected')}
                        </p>
                        <p className="text-xs text-orange-700 mt-1">
                          {t('scheduleCall.availabilityCannotBeVerified')}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {formData.created_by && formData.scheduled_date && formData.scheduled_time && isHostConnected && (
                  <div className="space-y-2 p-4 border rounded-lg bg-muted/30">
                    {checkingAvailability && (
                      <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                        <Loader2 className="animate-spin h-4 w-4" />
                        <span>{t('scheduleCall.checkingAvailability')}</span>
                      </div>
                    )}

                    {availabilityStatus?.checked && !checkingAvailability && (
                      <div className="space-y-2">
                        {availabilityStatus.available ? (
                          <div className="flex items-center space-x-2 text-sm text-green-600">
                            <Check className="h-4 w-4" />
                            <span>{t('scheduleCall.hostAvailable')}</span>
                          </div>
                        ) : (
                          <div className="flex items-start space-x-2 text-sm text-orange-600">
                            <AlertCircle className="h-4 w-4 mt-0.5" />
                            <div>
                              <p className="font-medium">{t('scheduleCall.hostHasConflicts')}</p>
                              {availabilityStatus.conflicts && availabilityStatus.conflicts.length > 0 && (
                                <ul className="mt-1 space-y-1 text-xs">
                                  {availabilityStatus.conflicts.map((conflict: any, i: number) => (
                                    <li key={i}>• {conflict.summary || 'Busy'}</li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                <div className="space-y-2">
                  <Label>{t('scheduleCall.attendees')}</Label>
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <Input
                        type="email"
                        value={attendeeEmail}
                        onChange={(e) => setAttendeeEmail(e.target.value)}
                        placeholder="email@example.com"
                        className="flex-1"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          if (attendeeEmail && !formData.attendees.includes(attendeeEmail)) {
                            setFormData({ ...formData, attendees: [...formData.attendees, attendeeEmail] });
                            setAttendeeEmail('');
                          }
                        }}
                      >
                        <PlusIcon className="w-4 h-4" />
                      </Button>
                    </div>
                    {formData.attendees.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {formData.attendees.map((email, index) => (
                          <Badge key={index} variant="secondary" className="flex items-center gap-1">
                            <Users className="w-3 h-3" />
                            {email}
                            <button
                              type="button"
                              onClick={() => {
                                setFormData({
                                  ...formData,
                                  attendees: formData.attendees.filter((_, i) => i !== index)
                                });
                              }}
                              className="ml-1 hover:text-destructive"
                            >
                              ×
                            </button>
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{t('scheduleCall.description')}</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    placeholder={t('scheduleCall.placeholders.description')}
                  />
                </div>

                <div className="space-y-2">
                  <Label>{t('scheduleCall.meetingUrl')}</Label>
                  <Input
                    type="url"
                    value={formData.meeting_url}
                    onChange={(e) => setFormData({ ...formData, meeting_url: e.target.value })}
                    placeholder={t('scheduleCall.placeholders.meetingUrl')}
                  />
                  <p className="text-xs text-muted-foreground">
                    💡 {t('scheduleCall.leaveEmptyForGoogleCalendar')}
                  </p>
                </div>

                {isHostConnected && (
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="google-invite"
                      checked={sendGoogleInvite}
                      onCheckedChange={(checked) => setSendGoogleInvite(checked as boolean)}
                    />
                    <Label htmlFor="google-invite" className="text-sm font-normal cursor-pointer">
                      {t('scheduleCall.sendGoogleCalendarInvite')}
                    </Label>
                  </div>
                )}
              </>
            )}

            {/* COMMON FIELDS - Date and Time */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('scheduleCall.date')} *</Label>
                <DatePickerInput
                  value={formData.scheduled_date ?? null}
                  onChange={(date) => {
                    setFormData({ ...formData, scheduled_date: date ?? undefined });
                    setFieldErrors(prev => ({ ...prev, scheduled_date: false }));
                  }}
                  minDate={(() => { const t = new Date(); t.setHours(0,0,0,0); return t; })()}
                />
                {fieldErrors.scheduled_date && (
                  <p className="text-xs text-red-500">{t('scheduleCall.errors.dateRequired')}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label>{t('scheduleCall.time')} *</Label>
                <Input
                  type="time"
                  value={formData.scheduled_time}
                  onChange={(e) => {
                    setFormData({ ...formData, scheduled_time: e.target.value });
                    setFieldErrors(prev => ({ ...prev, scheduled_time: false }));
                  }}
                  className={cn(fieldErrors.scheduled_time && "border-red-500")}
                />
                {fieldErrors.scheduled_time && (
                  <p className="text-xs text-red-500">{t('scheduleCall.errors.timeRequired')}</p>
                )}
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
              >
                {t('common.cancel')}
              </Button>
              {formData.channel === 'video' && (
                <Button
                  onClick={handleStartVideoRingNow}
                  disabled={isStartingVideoRing || !formData.title || (!selectedContact && !selectedLead)}
                  variant="default"
                  className="bg-orange-600 hover:bg-orange-700"
                >
                  {isStartingVideoRing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  {!isStartingVideoRing && <Zap className="w-4 h-4 mr-2" />}
                  {t('scheduleCall.startVideoCallNow')}
                </Button>
              )}
              <Button
                onClick={handleSubmit}
                disabled={isSubmitting}
              >
                {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {formData.channel === 'video' ? t('scheduleCall.scheduleMeeting') : t('scheduleCall.scheduleCall')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <MeetingConfirmationModal
        isOpen={showConfirmation}
        onClose={() => {
          setShowConfirmation(false);
          setConfirmedMeeting(null);
        }}
        meeting={confirmedMeeting}
      />

      {videoRingData && (
        <>
          {console.log('[ScheduleCall] 🎬 Rendering VideoRingProgressModal with data:', videoRingData)}
          <VideoRingProgressModal
            ringRequestId={videoRingData.ring_request_id}
            initialLevel={videoRingData.current_level}
            initialTimeout={videoRingData.timeout_seconds}
            initialUsers={videoRingData.available_users}
            onClose={() => {
              setVideoRingData(null);
            }}
          />
        </>
      )}
    </>
  );
}
